import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { maskitoParseNumber } from '@maskito/kit';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { PredefinedInputMaskOptions } from '@blaze-shared/shared-ui';
import {
  DEFAULT_NUMBER_PRECISION,
  isNullish,
  isNullishOrEmptyString,
} from '@blaze-shared/shared-util';

import { type InlineEditorConfig } from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-number-editor',
  templateUrl: './number-editor.component.html',
  styleUrls: ['./number-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableNumberEditorComponent implements OnInit, AfterViewInit {
  valueChange = output<string | null>();

  @Input() config?: InlineEditorConfig;

  @ViewChild('stringInput') stringInput!: ElementRef<HTMLInputElement>;

  formControl = new FormControl('');
  maskOptions: PredefinedInputMaskOptions | null = null;

  @Input() set value(val: number | string | undefined) {
    if (isNullish(val) || typeof val === 'string') {
      return;
    }
    this.formControl.patchValue(val.toString());
  }

  constructor() {
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (isNullishOrEmptyString(value)) {
        this.emitValue(null);
        return;
      }

      let parsed: number | null = maskitoParseNumber(value);
      if (isNaN(parsed)) {
        parsed = 0;
      }
      this.emitValue(parsed.toString());
    });
  }

  ngOnInit(): void {
    const thousandSeparator =
      new Intl.NumberFormat().formatToParts(0).find((part) => part.type === 'group')?.value ?? '';
    const precision: number = Number.isInteger(this.config?.columnSchema?.precision)
      ? (this.config?.columnSchema?.precision ?? DEFAULT_NUMBER_PRECISION)
      : DEFAULT_NUMBER_PRECISION;

    this.maskOptions = {
      numberFormat: {
        format: 'number',
        thousandSeparator,
        precision,
      },
    };
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.stringInput.nativeElement.focus();
      this.stringInput.nativeElement.select();
    });
  }

  private emitValue(value: string | null): void {
    this.valueChange.emit(value === '' ? null : value);
  }
}
